import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators, Dispatch } from 'redux';
import { StoreModel } from 'models/StoreModel';
import { Translations } from 'models/Translations';
import { Translate } from 'infrastructure/mapping/Translate';
import { TranslationsActions } from 'redux/actions/TranslationActions';
import { LegalLinks } from 'app/pages/misc/Legal';

interface Props {
    translations: Translations | null;
    loadTranslations: typeof TranslationsActions.loadTranslations;
}

const LandingComp: React.FC<Props> = ({ translations, loadTranslations }) => {
    React.useEffect(() => {
        if (!translations) {
            loadTranslations();
        }
    }, [translations, loadTranslations]);
    // <base href="/"> would resolve a bare "#id" to the root (login), so anchor to this page
    const here = window.location.pathname;
    const t = (text: string) => translations ? Translate.text(translations, text) : text;

    return (
        <div className="c-landing">
            <div className="c-landing__wrap">
                <header className="c-landing__top">
                    <a className="c-landing__brand" href={here + "#top"}><b>MOPS<i>.</i></b><span>Machine Operating and Planning System</span></a>
                    <nav className="c-landing__nav" aria-label={t("Main")}>
                        <a className="c-landing__link" href={here + "#how"}>{t("How it works")}</a>
                        <a className="c-landing__link" href={here + "#features"}>{t("Features")}</a>
                        <a className="c-landing__link" href={here + "#compare"}>{t("Compare")}</a>
                        <Link className="c-landing__btn c-landing__btn--ghost" to="/account/login">{t("Log in")}</Link>
                    </nav>
                </header>

                <div id="top" className="c-landing__hero">
                    <div className="c-landing__copy">
                        <p className="c-landing__eyebrow">{t("For construction, farm and municipal fleets")}</p>
                        <h1>{t("Hours, fuel and defects.")} <em>{t("One daily report.")}</em></h1>
                        <p className="c-landing__lead">{t("Your operators log the hour meter, the fuel stop and anything broken in one short form. MOPS turns those readings into due dates for every service interval, so the workshop knows what to do next. No trackers to install.")}</p>
                        <div className="c-landing__ctas">
                            <Link className="c-landing__btn c-landing__btn--primary" to="/Account/register">{t("Create your account")}</Link>
                            <a className="c-landing__btn c-landing__btn--ghost" href={here + "#how"}>{t("See how it works")}</a>
                        </div>
                        <p className="c-landing__fine">{t("Runs in any browser, on the phone in the cab or the PC in the office. German and English. Hosted in the EU, GDPR (DSGVO) compliant.")}</p>
                    </div>

                    <figure className="c-logbook" aria-label={t("Example daily report in MOPS")}>
                        <header>
                            <div><small>{t("Daily report · example")}</small><strong>{t("Wheel loader WL-07")}</strong></div>
                            <div className="c-logbook__meter" aria-label={t("Hour meter 4812.6 hours")}>
                                <span>4</span><span>8</span><span>1</span><span>2</span><span className="is-decimal">6</span><em>{t("h")}</em>
                            </div>
                        </header>
                        <dl className="c-logbook__rows">
                            <div className="c-logbook__row"><dt>{t("Operator")}</dt><dd>J. Berger · 14.10.2026</dd></div>
                            <div className="c-logbook__row"><dt>{t("Fuel")}</dt><dd><span className="c-logbook__num">86 L</span> {t("diesel")} <span className="c-logbook__muted">· <span className="c-logbook__num">{t("1,140 L")}</span> {t("per 100 h avg.")}</span></dd></div>
                            <div className="c-logbook__row"><dt>{t("Defect")}</dt><dd>{t("Hydraulic hose leaking at boom")} <span className="c-logbook__pill">{t("Out of order")}</span></dd></div>
                        </dl>
                        <div className="c-logbook__due">
                            <p className="c-logbook__due-title">{t("Upcoming maintenance")}</p>
                            <div>
                                <div className="c-logbook__due-item"><b>{t("500 h service")}</b><span className="c-logbook__num">{t("due in 37 h")}</span></div>
                                <div className="c-logbook__bar"><i style={{ width: '93%' }}></i></div>
                            </div>
                            <div>
                                <div className="c-logbook__due-item"><b>{t("Annual safety inspection")}</b><span className="c-logbook__num">{t("due in 64 days")}</span></div>
                                <div className="c-logbook__bar"><i className="is-ok" style={{ width: '82%' }}></i></div>
                            </div>
                        </div>
                    </figure>
                </div>

                <section id="how">
                    <div className="c-landing__head">
                        <p className="c-landing__eyebrow">{t("How it works")}</p>
                        <h2>{t("From the cab to the workshop")}</h2>
                    </div>
                    <ol className="c-landing__steps">
                        <li>
                            <span className="c-landing__step">{t("Step")} 1</span>
                            <h3>{t("The operator reports")}</h3>
                            <p>{t("At the end of the shift: hour-meter reading, fuel stop and any defects. A photo of the hour meter or the fuel receipt fills in the number. Their machine is already selected, and a defect can mark it out of order straight away.")}</p>
                        </li>
                        <li>
                            <span className="c-landing__step">{t("Step")} 2</span>
                            <h3>{t("MOPS plans the service")}</h3>
                            <p>{t("Each maintenance has an interval in operating hours or in days. MOPS counts down from the latest reading and flags work that is due or overdue.")}</p>
                        </li>
                        <li>
                            <span className="c-landing__step">{t("Step")} 3</span>
                            <h3>{t("The workshop closes it")}</h3>
                            <p>{t("Mechanics see open defects and due maintenance in one workshop view, log what they did and which parts they used. It all lands in the machine's history.")}</p>
                        </li>
                    </ol>
                </section>
            </div>

            <section id="features" className="c-landing__band">
                <div className="c-landing__wrap">
                    <div className="c-landing__head">
                        <p className="c-landing__eyebrow">{t("Features")}</p>
                        <h2>{t("What MOPS keeps track of")}</h2>
                    </div>
                    <div className="c-landing__features">
                        <div><h3>{t("Hour-based maintenance")}</h3><p>{t("Intervals by operating hours or calendar days, with an upcoming list that shows what is due and what is overdue.")}</p></div>
                        <div><h3>{t("Fuel per operating hour")}</h3><p>{t("Every fuel stop with fuel type and quantity. MOPS charts consumption per 100 operating hours for each machine.")}</p></div>
                        <div><h3>{t("Defects and downtime")}</h3><p>{t("Operators describe the problem and flag the machine out of order. Open defects stay visible until the workshop fixes them.")}</p></div>
                        <div><h3>{t("Machine history")}</h3><p>{t("Reports, fuel stops, defects and maintenance in one timeline per machine, for audits, resale and warranty claims.")}</p></div>
                        <div><h3>{t("Tasks for the team")}</h3><p>{t("Personal and shared task lists, so the jobs that are not maintenance do not get lost either.")}</p></div>
                        <div><h3>{t("Your whole fleet")}</h3><p>{t("Any brand and age: machines, types, manufacturers, parts and fuel types in one place, with roles for operators, mechanics and admins.")}</p></div>
                    </div>
                </div>
            </section>

            <div className="c-landing__wrap">
                <section id="compare">
                    <div className="c-landing__head">
                        <p className="c-landing__eyebrow">{t("Compare")}</p>
                        <h2>{t("Perfect for fleets without telematics, too")}</h2>
                        <p className="c-landing__lead">{t("Telematics platforms need a tracker in every machine. Maintenance software starts at the work order. Check apps stop at the defect. MOPS covers the whole run from the operator's reading to the finished service.")}</p>
                    </div>
                    <div className="c-landing__table">
                        <table>
                            <thead>
                                <tr><th scope="col"></th><th scope="col" className="is-us">MOPS</th><th scope="col">{t("Telematics platforms")}</th><th scope="col">{t("Maintenance software (CMMS)")}</th><th scope="col">{t("Operator check apps")}</th></tr>
                            </thead>
                            <tbody>
                                <tr><th scope="row">{t("Hours, fuel and defects in one operator form")}</th><td className="is-us is-yes">{t("Yes")}</td><td className="is-no">{t("No, read from the tracker")}</td><td className="is-no">{t("No")}</td><td>{t("Defects only")}</td></tr>
                                <tr><th scope="row">{t("Service due by operating hours")}</th><td className="is-us is-yes">{t("Yes")}</td><td className="is-yes">{t("Yes")}</td><td>{t("Often calendar only")}</td><td className="is-no">{t("No")}</td></tr>
                                <tr><th scope="row">{t("Fuel per operating hour")}</th><td className="is-us is-yes">{t("Yes")}</td><td>{t("With fuel sensors")}</td><td className="is-no">{t("No")}</td><td className="is-no">{t("No")}</td></tr>
                                <tr><th scope="row">{t("Workshop view for defects and maintenance")}</th><td className="is-us is-yes">{t("Yes")}</td><td>{t("Varies")}</td><td className="is-yes">{t("Yes")}</td><td className="is-no">{t("No")}</td></tr>
                                <tr><th scope="row">{t("Hardware needed")}</th><td className="is-us is-yes">{t("None")}</td><td>{t("A tracker per machine")}</td><td className="is-yes">{t("None")}</td><td className="is-yes">{t("None")}</td></tr>
                                <tr><th scope="row">{t("Start without a sales call")}</th><td className="is-us is-yes">{t("Yes")}</td><td className="is-no">{t("Usually not")}</td><td>{t("Some vendors")}</td><td>{t("Some vendors")}</td></tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="c-landing__note">{t("Based on the public product pages of seven European vendors, checked in October 2026. Categories describe typical products; single vendors may differ.")}</p>
                </section>

                <section id="faq">
                    <div className="c-landing__head">
                        <p className="c-landing__eyebrow">{t("Questions")}</p>
                        <h2>{t("Before you start")}</h2>
                    </div>
                    <div className="c-landing__faq">
                        <details><summary>{t("Do I need trackers or sensors in my machines?")}</summary><p>{t("No. Operators type in the hour-meter reading and the fuel stop, or take a photo and MOPS reads the number. That works for every machine, from a 30-year-old excavator to a new tractor.")}</p></details>
                        <details><summary>{t("We already have trackers. Can MOPS read them?")}</summary><p>{t("On request. We connect telematics providers case by case; many manufacturers offer the ISO 15143-3 interface for operating hours.")}</p></details>
                        <details><summary>{t("Is there an app to install?")}</summary><p>{t("MOPS runs in the browser on phones, tablets and PCs. Open it, log in and save it to the home screen.")}</p></details>
                        <details><summary>{t("Where is my data stored?")}</summary><p>{t("On servers in the European Union. MOPS is run in line with the GDPR (DSGVO), and we sign a data processing agreement (AVV) with every customer.")}</p></details>
                        <details><summary>{t("Which languages are available?")}</summary><p>{t("German and English. Each user can choose their own.")}</p></details>
                        <details><summary>{t("How is MOPS priced?")}</summary><p>{t("Plans are set by the number of machines and users in your fleet. Tasks and defect tracking are part of the plan.")}</p></details>
                    </div>
                </section>

                <section>
                    <div className="c-landing__closing">
                        <h2>{t("Put your fleet's logbook online")}</h2>
                        <div className="c-landing__ctas">
                            <Link className="c-landing__btn c-landing__btn--primary" to="/Account/register">{t("Create your account")}</Link>
                            <Link className="c-landing__btn c-landing__btn--ghost" to="/account/login">{t("Log in")}</Link>
                        </div>
                    </div>
                </section>

                <footer className="c-landing__footer">
                    <span>MOPS · Machine Operating and Planning System</span>
                    <span>{t("Example figures above are for illustration.")}</span>
                    <nav className="c-landing__legal-links" aria-label="Rechtliches"><LegalLinks /></nav>
                </footer>
            </div>
        </div>
    );
};

function mapStateToProps(state: StoreModel) {
    return {
        translations: state.translations,
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        loadTranslations: bindActionCreators(TranslationsActions.loadTranslations, dispatch),
    };
}
export const Landing = connect(mapStateToProps, mapDispatchToProps)(LandingComp);
